
import { supabase } from "@/integrations/supabase/client";
import { CONTENT_MODERATION_CONFIG } from '@/config/contentModerationConfig';

export interface EnhancedModerationResult {
  isAppropriate: boolean;
  reason?: string;
  confidence: number;
  details: {
    adult: string;
    racy: string;
    violence: string;
    medical: string;
    spoof: string;
  };
  applestoreCompliant: boolean;
  flaggedReasons: string[];
}

export const validateImageContentEnhanced = async (file: File, userId: string): Promise<EnhancedModerationResult> => {
  try {
    console.log('🔍 Starting enhanced content validation for:', file.name, 'Type:', file.type, 'Size:', file.size);
    
    // Enhanced file type validation with HEIC/HEIF support
    const allowedTypes = [
      'image/jpeg', 
      'image/jpg', 
      'image/png', 
      'image/webp',
      'image/heic',
      'image/heif',
      'image/heic-sequence',
      'image/heif-sequence'
    ];
    
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'];
    
    // Check MIME type first, then fallback to extension for HEIC files
    const isValidType = allowedTypes.includes(file.type);
    const fileName = file.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
    
    if (!isValidType && !hasValidExtension) {
      console.log('❌ File type validation failed:', file.type, 'for file:', file.name);
      const result: EnhancedModerationResult = {
        isAppropriate: false,
        reason: 'Invalid file type. Please upload a JPEG, PNG, WebP, or HEIC image.',
        confidence: 1.0,
        details: { adult: 'UNKNOWN', racy: 'UNKNOWN', violence: 'UNKNOWN', medical: 'UNKNOWN', spoof: 'UNKNOWN' },
        applestoreCompliant: false,
        flaggedReasons: ['Invalid file type']
      };
      await logModerationResult(userId, file.name, result, 'blocked');
      return result;
    }

    // Size check (stricter for Apple Store compliance)
    const maxSize = CONTENT_MODERATION_CONFIG.appleStoreCompliance.enabled ? 8 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      console.log('❌ File size validation failed:', file.size, 'max:', maxSize);
      const result: EnhancedModerationResult = {
        isAppropriate: false,
        reason: `File size too large. Please choose an image smaller than ${maxSize / (1024 * 1024)}MB.`,
        confidence: 1.0,
        details: { adult: 'UNKNOWN', racy: 'UNKNOWN', violence: 'UNKNOWN', medical: 'UNKNOWN', spoof: 'UNKNOWN' },
        applestoreCompliant: false,
        flaggedReasons: ['File too large']
      };
      await logModerationResult(userId, file.name, result, 'blocked');
      return result;
    }
    
    console.log('✅ File validation passed, converting to base64...');
    
    // Convert to base64
    const imageData = await fileToBase64(file);
    
    console.log('📡 Calling enhanced moderation function...');
    
    // Call enhanced moderation function
    const { data, error } = await supabase.functions.invoke('enhanced-content-moderation', {
      body: { 
        imageData, 
        userId,
        config: CONTENT_MODERATION_CONFIG
      }
    });

    if (error) {
      console.error('Enhanced moderation function error:', error);
      const result: EnhancedModerationResult = {
        isAppropriate: false,
        reason: 'Unable to verify image content. Please try again or choose a different image.',
        confidence: 0.9,
        details: { adult: 'UNKNOWN', racy: 'UNKNOWN', violence: 'UNKNOWN', medical: 'UNKNOWN', spoof: 'UNKNOWN' },
        applestoreCompliant: false,
        flaggedReasons: ['Service unavailable']
      };
      await logModerationResult(userId, file.name, result, 'blocked');
      return result;
    }

    console.log('✅ Enhanced moderation result:', data);
    
    // Log the result
    await logModerationResult(userId, file.name, data, data.isAppropriate ? 'approved' : 'blocked');
    
    return data;
    
  } catch (error) {
    console.error('Enhanced content validation error:', error);
    
    // Check if this is a HEIC-specific error
    const isHEICFile = file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif') || 
                       file.type === 'image/heic' || file.type === 'image/heif';
    
    const result: EnhancedModerationResult = {
      isAppropriate: false,
      reason: isHEICFile 
        ? 'HEIC files from iPhone may not be supported in this browser. Please try converting to JPEG first or use a different browser.'
        : 'Content validation failed. Please try again or select a different image.',
      confidence: 0.9,
      details: { adult: 'UNKNOWN', racy: 'UNKNOWN', violence: 'UNKNOWN', medical: 'UNKNOWN', spoof: 'UNKNOWN' },
      applestoreCompliant: false,
      flaggedReasons: isHEICFile ? ['HEIC processing failed'] : ['Validation failed']
    };
    await logModerationResult(userId, file.name, result, 'blocked');
    return result;
  }
};

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => {
      console.error('FileReader error:', error);
      reject(error);
    };
    reader.readAsDataURL(file);
  });
};

const logModerationResult = async (
  userId: string, 
  fileName: string, 
  result: EnhancedModerationResult, 
  action: string
) => {
  try {
    await supabase
      .from('content_moderation_logs' as any)
      .insert({
        user_id: userId,
        photo_url: fileName,
        moderation_result: result,
        action_taken: action,
        flagged_reasons: result.flaggedReasons,
        apple_store_compliant: result.applestoreCompliant
      });
  } catch (error) {
    console.error('Failed to log moderation result:', error);
  }
};
